"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { useStoreActions } from "easy-peasy";
import { getCart } from "@/actions/cart";
import type { StoreModel } from "@/store";

/**
 * On mount (when the user is authenticated), fetches the DB cart and
 * hydrates the easy-peasy store, replacing any stale local state.
 * Must be rendered inside both <SessionProvider> and <StoreProvider>.
 */
export function CartSyncProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();

  const hydrateCart = useStoreActions(
    (actions: StoreModel) => actions.cart.hydrateCart,
  );
  const setLoaded = useStoreActions(
    (actions: StoreModel) => actions.cart.setLoaded,
  );

  useEffect(() => {
    if (status === "loading") return;

    if (status === "unauthenticated" || !session?.user) {
      setLoaded(true);
      return;
    }

    if (status === "authenticated" && session?.user) {
      getCart()
        .then((dbItems) => {
          hydrateCart(
            dbItems.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
              plan: item.plan ?? undefined,
            })),
          );
        })
        .catch(() => {
          // If fetch fails, keep local items and mark as loaded
          setLoaded(true);
        });
    }
  }, [status, session, hydrateCart, setLoaded]);

  return <>{children}</>;
}
